import { MeasurementScale } from "@repo/app-types/entities/interview";
import type { ReportKeyMetricData } from "@repo/app-types/entities/report";
import { renderPlural } from "@repo/app-types/helpers/utilities";
import { Size } from "@repo/ui/helpers/helpers";
import type { ReactNode } from "react";
import { LabeledAssessmentScore } from "../insights/assessmentScore.js";
import { GradingPassRatePill } from "../teammatePerformance/gradingPassRatePill.js";

export function renderValueAsWidget(data: {
  value: number | null;
  valueType:
    | "passRate"
    | "percentage"
    | "calls"
    | "teammates"
    | "gradings"
    | "weeks"
    | "oneToFiveScoreAverage"
    | "usd"
    | "years";
  isLowerBound?: boolean;
}) {
  const { value, valueType } = data;

  if (value === null) {
    return "—";
  }

  switch (valueType) {
    case "passRate":
      return <GradingPassRatePill size={Size.SMALL} passRate={value} />;

    case "oneToFiveScoreAverage":
      return (
        <LabeledAssessmentScore
          score={{ value, scale: MeasurementScale.FivePointInterval }}
        />
      );

    default:
      return renderValueAsString(data);
  }
}

export function renderValueAsString(data: {
  value: number | null;
  valueType:
    | "passRate"
    | "percentage"
    | "calls"
    | "teammates"
    | "gradings"
    | "weeks"
    | "oneToFiveScoreAverage"
    | "usd"
    | "years";
  isLowerBound?: boolean;
  unitDisplay?: "none" | "long";
}) {
  const { value, valueType, isLowerBound, unitDisplay } = data;

  if (value === null) {
    return "—";
  }

  const lowerBoundSuffix = isLowerBound ? "+" : "";

  switch (valueType) {
    case "passRate":
    case "percentage":
      return (
        value.toLocaleString("en-US", {
          style: "percent",
          maximumFractionDigits: 1,
        }) + lowerBoundSuffix
      );

    case "calls": {
      const number = value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });

      switch (unitDisplay) {
        case "long":
          return renderPlural(Math.round(value) + (isLowerBound ? 1 : 0), {
            one: `${number} call`,
            other: () => `${number}${lowerBoundSuffix} calls`,
          });

        default:
          return number + lowerBoundSuffix;
      }
    }
    case "teammates": {
      const number = value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });

      switch (unitDisplay) {
        case "long":
          return renderPlural(Math.round(value) + (isLowerBound ? 1 : 0), {
            one: `${number} teammate`,
            other: () => `${number}${lowerBoundSuffix} teammates`,
          });

        default:
          return number + lowerBoundSuffix;
      }
    }
    case "weeks": {
      const number = value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });

      switch (unitDisplay) {
        case "long":
          return renderPlural(Math.round(value) + (isLowerBound ? 1 : 0), {
            one: `${number} week`,
            other: () => `${number}${lowerBoundSuffix} weeks`,
          });

        default:
          return number + lowerBoundSuffix;
      }
    }
    case "years": {
      const number = value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });

      switch (unitDisplay) {
        case "long":
          return renderPlural(Math.round(value) + (isLowerBound ? 1 : 0), {
            one: `${number} year`,
            other: () => `${number}${lowerBoundSuffix} years`,
          });

        default:
          return number + lowerBoundSuffix;
      }
    }
    case "oneToFiveScoreAverage": {
      const number = value.toLocaleString("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

      return data.unitDisplay === "long"
        ? `${number}${lowerBoundSuffix}/5`
        : `${number}${lowerBoundSuffix}`;
    }
    case "usd": {
      const number = value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumSignificantDigits: 3,
        notation: "compact",
      });

      return number + lowerBoundSuffix;
    }
    default:
      return value.toLocaleString("en-US") + lowerBoundSuffix;
  }
}

export function renderPreviousValue(data: {
  previousValue: number | null;
  valueType: ReportKeyMetricData["valueType"];
}) {
  const { previousValue, valueType } = data;

  if (previousValue === null) {
    return null;
  }

  return (
    <>
      vs. {renderValueAsString({ value: previousValue, valueType })} prior
      period
    </>
  );
}

interface ValueChangeDirection {
  type: "up" | "down" | "neutral";
  symbol: string | null;
}

function computeValueChangeDirection(change: number): ValueChangeDirection {
  if (change === 0) {
    return {
      type: "neutral" as const,
      symbol: null,
    };
  }

  if (change > 0) {
    return {
      type: "up" as const,
      symbol: "▲",
    };
  }

  return {
    type: "down" as const,
    symbol: "▼",
  };
}

interface ValueChangeIndication {
  type: "positive" | "negative" | "neutral";
  textClassName: string;
  sparklineColor: string;
}

export function computeValueChangeIndication(
  change: number,
  indicatorType: ReportKeyMetricData["indicatorType"],
): ValueChangeIndication {
  if (change === 0 || indicatorType === "neutral") {
    return {
      type: "neutral" as const,
      textClassName: "text-gray-600",
      sparklineColor: "var(--color-gray-600)",
    };
  }

  const isPositiveChange = change > 0;
  const isPositiveIndicator = indicatorType === "positive";

  return isPositiveIndicator === isPositiveChange
    ? {
        type: "positive" as const,
        textClassName: "text-success",
        sparklineColor: "var(--color-green-600)",
      }
    : {
        type: "negative" as const,
        textClassName: "text-destructive",
        sparklineColor: "var(--color-red-600)",
      };
}

export function computeValueChange(data: {
  value: number | null;
  previousValue: number | null;
  valueType:
    | "passRate"
    | "percentage"
    | "calls"
    | "teammates"
    | "gradings"
    | "weeks"
    | "oneToFiveScoreAverage"
    | "usd"
    | "years";
  indicatorType: "neutral" | "positive" | "negative";
}): {
  difference: number | null;
  direction: ValueChangeDirection;
  indication: ValueChangeIndication;
  content: ReactNode;
} {
  const { value, previousValue, valueType } = data;

  if (value === null || previousValue === null) {
    const direction = computeValueChangeDirection(0);
    const indication = computeValueChangeIndication(0, data.indicatorType);

    return {
      difference: null,
      direction,
      indication,
      content: null,
    };
  }

  switch (valueType) {
    case "passRate":
    case "percentage": {
      const difference = Math.round((value - previousValue) * 1000) / 1000;

      const direction = computeValueChangeDirection(difference);
      const indication = computeValueChangeIndication(
        difference,
        data.indicatorType,
      );

      return {
        difference,
        direction,
        indication,
        content: (
          <span className={indication.textClassName}>
            {direction.type === "neutral" ? (
              "No change"
            ) : (
              <>
                {direction.symbol}{" "}
                {(Math.abs(difference) * 100).toLocaleString("en-US", {
                  maximumFractionDigits: 1,
                })}
                pp
              </>
            )}
          </span>
        ),
      };
    }

    default: {
      const relativeDifference =
        Math.round(((value - previousValue) / previousValue) * 100) / 100;

      const direction = computeValueChangeDirection(relativeDifference);
      const indication = computeValueChangeIndication(
        relativeDifference,
        data.indicatorType,
      );

      return {
        difference: relativeDifference,
        direction,
        indication,
        content: (
          <span className={indication.textClassName}>
            {direction.type === "neutral" ? (
              "No change"
            ) : (
              <>
                {direction.symbol}{" "}
                {isFinite(relativeDifference)
                  ? Math.abs(relativeDifference).toLocaleString("en-US", {
                      style: "percent",
                      maximumFractionDigits: 0,
                    })
                  : "∞"}
              </>
            )}
          </span>
        ),
      };
    }
  }
}
